import React, { useState } from 'react';

import {
  DownloadOutlined,
  FilterOutlined,
  ReconciliationOutlined,
} from '@ant-design/icons';
import { Col, Dropdown as DropdownAntD, Input, Menu, Select } from 'antd';
import moment from 'moment';
import { T } from 'react-translator-component';

import {
  ButtonDropdown,
  Container,
  FilterButton,
  FilterContainer,
  RangerPicker,
} from './styled';

import { getLastThreeMonths } from '../../../utils/dates';
import { hasReconciliationRole } from '../../../utils/hasReconciliationRole/hasReconciliationRole';
import { capitalizeFirstLetter } from '../../../utils/strings';
import { OrderByType } from '../../organisms/FinancialTable/FinancialTable';
import { PaymentStatus, PaymentStatusText } from '../../organisms/FinancialTable/useFinancialTable';

const Dropdown: any = DropdownAntD;

interface FilterDateProps {
  startDate: string;
  endDate: string;
  tenantId?: string;
  chargeBoxId?: string;
  paymentStatus?: string;
}

type Tab = 'allMovements' | 'recharges' | 'deposits';

const paymentStatusOptions = Object.values(PaymentStatus);

interface FinancialHeaderProps {
  transactionPkSearch: string;
  filterDate: FilterDateProps;
  orderBy: OrderByType;
  onFilter: (shouldResetPage?: boolean) => void;
  setFilterDate: React.Dispatch<React.SetStateAction<FilterDateProps>>;
  setTransactionPkSearch: React.Dispatch<React.SetStateAction<string>>;
  setOrderBy: React.Dispatch<React.SetStateAction<OrderByType>>;
  downloadTransactions: (
    tab: Tab,
    startDate: string,
    endDate: string,
    transactionPk?: string,
    orderBy?: string,
    tenantId?: string,
    chargeBoxId?: string,
    paymentStatus?: string,
    shouldShowBeneficiaryAccounts?: boolean,
  ) => void;
  tab: Tab;
  tenants: any;
  chargeboxes: any;
  shouldShowBeneficiaryAccounts: boolean;
  onReconcileClick?: () => void;
}

const FinancialHeader = ({
  transactionPkSearch,
  filterDate,
  orderBy,
  setFilterDate,
  setTransactionPkSearch,
  setOrderBy,
  onFilter,
  downloadTransactions,
  tab,
  tenants,
  chargeboxes,
  shouldShowBeneficiaryAccounts,
  onReconcileClick,
}: FinancialHeaderProps) => {
  const START = moment(new Date(moment().subtract(20, 'days') as any)).format(
    'YYYY-MM-DD',
  );

  const END = moment(new Date(moment() as any)).format('YYYY-MM-DD');

  const RANGE_LIMIT_DAYS = 365;

  const [showRangePicker, setShowRangePicker] = useState(false);

  const [dates, setDates] = useState<any>([
    moment(filterDate.startDate),
    moment(filterDate.endDate),
  ]);
  const [value, setValue] = useState<any>([
    moment(filterDate.startDate),
    moment(filterDate.endDate),
  ]);

  const onOpenChange = (open) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const disabledDate = (current: any) => {
    if (!dates) {
      return false;
    }
    const tooLate =
      dates?.[0] && current.diff(dates?.[0], 'days') >= RANGE_LIMIT_DAYS;
    const tooEarly =
      dates?.[1] && dates?.[1].diff(current, 'days') >= RANGE_LIMIT_DAYS;
    return !!tooEarly || !!tooLate || current.valueOf() > Date.now();
  };

  const generateStartDateAndEndDate = (date: string) => {
    const [month, year] = date.split('/');

    const fullYear = `20${year}`;

    const startDate = moment(`${month} ${fullYear}`, 'MMMM YYYY')
      .startOf('month')
      .format('YYYY-MM-DD');

    const endDate = moment(`${month} ${fullYear}`, 'MMMM YYYY')
      .endOf('month')
      .format('YYYY-MM-DD');

    return { startDate, endDate };
  };

  const handleChangeDateInputType = (value) => {
    if (value.value === 'custom') {
      setShowRangePicker(true);
    } else {
      const { startDate, endDate } = generateStartDateAndEndDate(value.value);

      setFilterDate({
        ...filterDate,
        startDate,
        endDate,
      });

      setShowRangePicker(false);
    }
  };

  const DropdownItems = () => (
    <Menu>
      <Menu.Item
        onClick={() =>
          downloadTransactions(
            tab,
            filterDate.startDate,
            filterDate.endDate,
            transactionPkSearch,
            orderBy.value,
            filterDate.tenantId,
            filterDate.chargeBoxId,
            filterDate.paymentStatus,
            shouldShowBeneficiaryAccounts,
          )
        }
      >
        {T('Download Excel')}
      </Menu.Item>
    </Menu>
  );

  return (
    <Container>
      <FilterContainer>
        {!showRangePicker && (
          <Select
            size='large'
            className='order-by-select'
            labelInValue={true}
            style={{ width: 200 }}
            value={{
              label: capitalizeFirstLetter(
                moment(filterDate.startDate).format('MMMM/YY'),
              ),
              value: capitalizeFirstLetter(
                moment(filterDate.startDate).format('MMMM/YY'),
              ),
            }}
            onChange={handleChangeDateInputType}
          >
            {getLastThreeMonths().map((month, index) => (
              <Select.Option value={month} key={`${month}-${index}`}>
                {month}
              </Select.Option>
            ))}

            <Select.Option value={'custom'}>Período customizado</Select.Option>
          </Select>
        )}

        {showRangePicker && (
          <RangerPicker
            format='DD/MM/YYYY'
            value={dates || value}
            disabledDate={disabledDate}
            onCalendarChange={(val) => setDates(val)}
            onChange={(val) => {
              setFilterDate({
                ...filterDate,
                startDate:
                  val?.length && val[0]
                    ? moment(new Date(val[0])).format('YYYY-MM-DD')
                    : START,
                endDate:
                  val?.length && val[1]
                    ? moment(new Date(val[1])).format('YYYY-MM-DD')
                    : END,
              });

              setValue(val);
            }}
            onOpenChange={onOpenChange}
            allowClear={false}
          />
        )}

        <Col className='order-by-col'>
          <Select
            size='large'
            className='order-by-select'
            value={{
              label: `${T('Order by:')} ${T(orderBy.label)}`,
              value: orderBy.value,
            }}
            labelInValue={true}
            onChange={(value) => {
              setOrderBy({ label: value.label, value: value.value });
            }}
          >
            <Select.Option value='CHRONOLOGICAL_ORDER'>
              {T('Chronological order')}
            </Select.Option>
            <Select.Option value='FINANCIAL_MOVEMENT'>
              {T('Financial movement')}
            </Select.Option>
          </Select>
        </Col>

        <Select
          className='order-by-select'
          size='large'
          style={{ width: '300px' }}
          onChange={(value: any) => {
            setFilterDate({ ...filterDate, tenantId: value });
          }}
          placeholder={T('Select Account')}
        >
          <Select.Option value=''>{T('All Accounts')}</Select.Option>

          {tenants?.map((tenant) => (
            <Select.Option value={tenant.apiKey} key={tenant.name}>
              {tenant.name}
            </Select.Option>
          ))}
        </Select>

        <Select
          showSearch
          size='large'
          style={{ width: '300px' }}
          onChange={(value) => {
            setFilterDate({ ...filterDate, chargeBoxId: value as any });
          }}
          placeholder='Selecione carregador'
          filterOption={(input, option) =>
            ((option?.key as string) ?? '').includes(input)
          }
        >
          <Select.Option value=''>Todos os carregadores</Select.Option>

          {chargeboxes?.map((chargebox) => (
            <Select.Option
              value={chargebox.ocppChargeBoxId}
              key={`${chargebox.chargeBoxCode}-${
                chargebox.description
              }-${Math.random()}`}
            >
              {chargebox.chargeBoxCode}
              {chargebox.description && ` - ${chargebox.description}`}
            </Select.Option>
          ))}
        </Select>

        <Input
          placeholder='Procure por ID ou e-mail'
          size='large'
          allowClear
          value={transactionPkSearch}
          className='search'
          onChange={(e) => {
            setTransactionPkSearch(e.target.value);
          }}
          style={{ width: '300px' }}
        />

        <Select
          size="large"
          style={{ width: 200 }}
          onChange={(value) =>
            setFilterDate({ ...filterDate, paymentStatus: value as any })
          }
          placeholder="Status"
        >
          <Select.Option value="">Todos os status</Select.Option>

          {paymentStatusOptions.map((status) => (
            <Select.Option key={status} value={status}>
              {PaymentStatusText[status]}
            </Select.Option>
          ))}
        </Select>

        <FilterButton icon={<FilterOutlined />} onClick={() => onFilter()}>
          Filtrar
        </FilterButton>

        <Dropdown
          overlay={DropdownItems}
          placement='bottomCenter'
          trigger={['click']}
        >
          <ButtonDropdown
            type='default'
            className='action_button'
            onClick={() => null}
          >
            <DownloadOutlined />
          </ButtonDropdown>
        </Dropdown>

        {onReconcileClick && hasReconciliationRole() && (
          <FilterButton
            type='primary'
            icon={<ReconciliationOutlined />}
            onClick={onReconcileClick}
            style={{ width: 'auto', paddingLeft: 16, paddingRight: 16 }}
          >
            Conciliar pagamentos
          </FilterButton>
        )}
      </FilterContainer>
    </Container>
  );
};

export default FinancialHeader;
